/**
 * Reactive notice for a task or build that has been Accepted or Initialising
 * longer than expected. Returns friendly copy with a suggested action once
 * the resource has been in its current pre-run status past the threshold
 * configured in STUCK_THRESHOLDS_MS; returns null otherwise.
 *
 * A single module-scoped useNow ticks every 30 seconds, so any number of
 * callers share one interval. The notice appears within ~30 seconds of the
 * threshold being crossed without requiring a status update from the API.
 */
import { computed, ComputedRef, MaybeRef, unref, watch } from "vue";
import { useNow } from "@vueuse/core";
import * as Sentry from "@sentry/vue";
import { Task, TaskStatus, TaskStatusE } from "@/types/api/tasks";
import { Build, BuildStatus, BuildStatusE } from "@/types/api/builds";
import { convertToMilliseconds } from "@/js/utilities";

export type StuckResourceKind = "task" | "build";

/**
 * Deduplicates Sentry reports for a given (kind, id, status) tuple across
 * all callers. The set is intentionally never trimmed — a single page
 * lifetime is short enough that the entries do not grow unbounded.
 */
const reportedStuck = new Set<string>();

function reportStuckOnce(kind: StuckResourceKind, id: string, status: string) {
	const key = `${kind}:${id}:${status}`;
	if (reportedStuck.has(key)) return;
	reportedStuck.add(key);
	Sentry.captureMessage(`Stuck ${kind}`, {
		level: "warning",
		tags: { resourceKind: kind, resourceID: id, status },
	});
}

const sharedNow = useNow({ interval: 30_000 });

const STUCK_THRESHOLDS_MS: Partial<Record<TaskStatus | BuildStatus, number>> = {
	[TaskStatusE.Accepted]: 5 * 60 * 1000,
	[TaskStatusE.Initialising]: 5 * 60 * 1000,
};

const STUCK_VERBS: Record<StuckResourceKind, Partial<Record<TaskStatus | BuildStatus, string>>> = {
	task: {
		[TaskStatusE.Accepted]: "queued",
		[TaskStatusE.Initialising]: "initialising",
	},
	build: {
		[BuildStatusE.Accepted]: "queued",
		[BuildStatusE.Initialising]: "initialising",
	},
};

export function useStuckResourceMessage(
	resource: MaybeRef<Partial<Task> | Partial<Build> | undefined>,
	kind: StuckResourceKind,
): ComputedRef<string | null> {
	const message = computed(() => {
		const r = unref(resource);
		const status = r?.Status;
		if (!status) return null;

		const threshold = STUCK_THRESHOLDS_MS[status];
		const verb = STUCK_VERBS[kind][status];
		if (threshold == null || verb == null) return null;

		const transition = r?.StatusTransitions?.findLast?.((st) => st.Status === status);
		if (transition?.Timestamp == null) return null;

		const enteredAtMs = convertToMilliseconds(transition.Timestamp);
		const elapsed = sharedNow.value.getTime() - enteredAtMs;
		if (elapsed < threshold) return null;

		return `This ${kind} has been ${verb} for longer than usual. Try cancelling and running it again.`;
	});

	watch(
		message,
		(val) => {
			if (!val) return;
			const r = unref(resource);
			const id = (r as Partial<Task>)?.TaskID ?? (r as Partial<Build>)?.BuildID;
			const status = r?.Status;
			if (!id || !status) return;
			reportStuckOnce(kind, id, status);
		},
		{ immediate: true },
	);

	return message;
}
